import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, TextField, Typography, Box, Button } from '@mui/material';
import Paper from '../../components/Paper';
import initState, { validateAll, initValidationStates } from '../../utils/stateHandler';
import SegmentSummary from '../../components/SegmentSummary';
import { createNewSegment, getCustomerCount, setTargetCustomerCount } from './createSegmentsReducer';
import { formRequestBody } from './helpers';
import { createCampaignQuery } from '../CreateCampaigns/constants';
import LogicalChooser from '../../components/LogicalChooser';
import { getAttributes } from '../CreateCampaigns/createCampaignReducer';
import styles from './createSegments.module.scss'
import { writeSummary } from '../../utils/utils';
import { showNavHeadersAction } from '../../components/NavBar/navBarReducer';
import { makeCustomerSegmentTree } from '../CreateCampaigns/helpers';

const defaultState = {
    segmentName: "",
    operator: "AND",
    attributes: [{}]
}

const defaultValidState = {
    segmentName: false,
    operator: true,
    attributes: false
}

const operatorSubAll = "(Include Business Ids that meet ALL of these conditions)"
const operatorSubAnyone = "(Include Business Ids that meet ANY ONE of these conditions)"
const optionHeader = "Customer Attributes"

const CreateSegments = () => {
    const store = useSelector(store => store.createCampaign)
    const dispatch = useDispatch()
    const [state, setters, handlers] = initState("Create Segment")(defaultState, validate)
    const [isValid, isValidSetters] = initValidationStates(defaultValidState, "Validations");
    const showSummary = useSelector(store => store.segmentSummary.showSummary)
    const customerCount = useSelector(store => store.createSegment.targetCustomerCount);
    const customerCountLoading = useSelector(store => store.createSegment.customerCountLoading);
    const customerCountError = useSelector(store => store.createSegment.customerCountError);

    let subText = operatorSubAll
    if (state.operator === "OR") {
        subText = operatorSubAnyone
    }

    const handleCreateSegment = () => {
        const segmentData = formRequestBody(state, store.attributeData.data)
        dispatch(createNewSegment(segmentData))
    }

    function validate(value, key) {
        let isValid = true
        switch (key) {
            case "segmentName":
                if (!value) {
                    isValid = false
                }
                break;
            case "attributes":
                isValid = validateAll(value, true)
                break;
            case "operator":
                isValid = true
                break;
        }
        isValidSetters[key].valid(isValid)
    }

    React.useEffect(() => {
        dispatch(getAttributes({ entityType: createCampaignQuery.customer }))
        dispatch(showNavHeadersAction())
        dispatch(setTargetCustomerCount(null));
    }, [dispatch])

    const fetchCustomerCount = () => {
        const customerSegmentQueryTree = makeCustomerSegmentTree(store.attributeData.data, state);
        if (customerCountLoading) return;
        dispatch(getCustomerCount(customerSegmentQueryTree));
    }
    
    const renderCountOrError = () => {
    if(!validateAll(isValid)) return null;
    if (!customerCountLoading && customerCountError) {
        return (
        <Grid item xs={12}>
            Try Again!
        </Grid>
        )
    }   
    if (!customerCountLoading && customerCount !== null) {
        return (
        <Grid item xs={12}>
            {customerCount} customers
        </Grid>
        )
    }      
    }

    return <>
        {showSummary ?
            <SegmentSummary /> : <Box sx={{ width: "calc(100% - 52px)", bgcolor: "#E5E5E5" }}>
                <Paper>
                    <Grid container className={styles.createSegmentContainer} justifyContent="space-between" alignItems="baseline">
                        <Grid container item xs={12}>
                            <Typography className={styles.summaryHeading}>Segment Name <span className={styles.imp}>*</span></Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField fullWidth multiline size='small' placeholder='Type here' type="text" rows={2} value={state.segmentName} onChange={handlers.segmentName} />
                        </Grid>
                    </Grid>
                </Paper>
                <Paper>
                    <Grid container className={styles.createSegmentContainer} justifyContent="space-between" alignItems="baseline">
                        <Grid container item xs={12}>
                            <Typography className={styles.heading}>Select Customer Target Group <span className={styles.imp}>*</span></Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <LogicalChooser data={store.attributeData.data} optionHeader={optionHeader} operatorSub={subText} parentState={state} updateParentState={setters} />
                        </Grid>
                    </Grid>
                </Paper>
                <Paper className={styles.createSegmentPaperContainer} extraRender={<Typography className={styles.heading}>Segement Summary</Typography>}>
                    <Grid container className={styles.createSegmentContainer} justifyContent="space-between" alignItems="baseline">
                        <Grid container item xs={12} justifyContent="space-between">
                            <Grid item xs={10}>
                                <Grid item xs={12} borderBottom="0.5px solid #9E9E9E">
                                    <Typography className={styles.summaryHeading}>{state.segmentName || "Segment Name"}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid item xs={8} marginTop="10px" whiteSpace="normal">
                                        {writeSummary(state)}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container item xs={2} textAlign="center">
                                {renderCountOrError()}
                                <Grid item xs={12} marginTop={'10px'}>
                                    <Button disabled={!validateAll(isValid)} onClick={fetchCustomerCount} className={styles.button} variant='contained'>{customerCountLoading ? 'Loading..' : 'Get Count'}</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
                <Box sx={{ width: "100%", bgcolor: "#E5E5E5", display: 'flex', flexDirection: 'row', pt: 2, justifyContent: "end", p: "10px 26px" }}>
                    <Button className={styles.buttons} disabled={!validateAll(isValid)} variant='contained' sx={{ color: "#FFF", padding: "6px 20px" }} onClick={handleCreateSegment}>
                        Create
                    </Button>
                </Box>
            </Box>}
    </>
}

export default CreateSegments